// Airbridge Web SDK wrapper.
//
// Centralised so every call site uses the same event taxonomy and so the
// integration is easy to evolve (Phase A.2 = server-side tracking links,
// Phase B = native Android SDK bridge).
//
// All public helpers MUST be safe to call before init() has completed and
// MUST never throw into application code — analytics errors should not
// break user flows.

import airbridge from 'airbridge-web-sdk-loader';
import { hasAnalyticsConsent } from './cookieConsent';

const APP_NAME = process.env.REACT_APP_AIRBRIDGE_APP_NAME;
const WEB_TOKEN = process.env.REACT_APP_AIRBRIDGE_WEB_SDK_TOKEN;

let initialized = false;

export function initAirbridge() {
  if (initialized) return;
  if (!APP_NAME || !WEB_TOKEN) {
    // Missing env vars (e.g. local dev without Airbridge configured).
    // Treat as a no-op: helpers below will be silent fallbacks.
    return;
  }
  // GDPR gate: never load attribution/analytics tracking before the user
  // has given explicit consent. The CookieConsentBanner will call
  // initAirbridge() again the moment consent is granted.
  if (!hasAnalyticsConsent()) {
    return;
  }
  try {
    airbridge.init({
      app: APP_NAME,
      webToken: WEB_TOKEN,
      autoStartTrackingEnabled: true,
      utmParsing: true,
    });
    initialized = true;
  } catch (err) {
    // Never let SDK init failure crash the app.
    console.warn('[airbridge] init failed:', err);
  }
}

export function isAirbridgeReady() {
  return initialized;
}

// ---------------------------------------------------------------------------
// User identity — call on every successful login and on logout.
// ---------------------------------------------------------------------------

export function identifyAirbridgeUser(user) {
  if (!initialized || !user) return;
  try {
    airbridge.setUserID(String(user.id || user._id || user.user_id || ''));
    if (user.email) airbridge.setUserEmail(user.email);
    const aliases = {};
    if (user.role) aliases.role = String(user.role);
    if (Object.keys(aliases).length) airbridge.setUserAlias(aliases);
  } catch (err) {
    console.warn('[airbridge] identify failed:', err);
  }
}

export function forgetAirbridgeUser() {
  if (!initialized) return;
  try {
    airbridge.clearUser();
  } catch (err) {
    console.warn('[airbridge] clearUser failed:', err);
  }
}

// ---------------------------------------------------------------------------
// Event tracking helpers.
//
// We use Airbridge Standard Events where possible (signup / signin) and
// custom event categories for invite lifecycle so they remain meaningful in
// the dashboard funnels and segments.
// ---------------------------------------------------------------------------

function sendEvent(category, payload = {}) {
  if (!initialized) return;
  // Respect post-grant consent revocation: if the user later changes their
  // mind via the Privacy settings re-open hook, stop sending events even
  // though the SDK is still loaded in memory.
  if (!hasAnalyticsConsent()) return;
  try {
    airbridge.events.send(category, payload);
  } catch (err) {
    console.warn('[airbridge] events.send failed:', category, err);
  }
}

export function trackSignup({ user, method = 'email', inviteContext = null } = {}) {
  if (!user) return;
  sendEvent('airbridge.user.signup', {
    semanticAttributes: {
      userID: String(user.id || user._id || ''),
      method,
      role: user.role || null,
      // Attribute the signup to the deep-link context that drove it (when
      // present). Drives downstream invite-funnel reporting.
      invite_id: inviteContext?.inviteId || null,
      invite_type: inviteContext?.inviteType || null,
    },
  });
}

export function trackSignin({ user, method = 'email' } = {}) {
  if (!user) return;
  sendEvent('airbridge.user.signin', {
    semanticAttributes: {
      userID: String(user.id || user._id || ''),
      method,
      role: user.role || null,
    },
  });
}

// Generic invite lifecycle events. `inviteType` differentiates the deep-link
// kind (host_to_guest, host_to_host, venue_staff, venue_event, …) so the
// same funnel can be partitioned in the Airbridge dashboard once Phase A
// expands to the other invite flows.
export function trackInviteViewed({ inviteId, inviteType = 'host_to_guest', extra = {} }) {
  if (!inviteId) return;
  sendEvent('invite_viewed', {
    semanticAttributes: { invite_id: inviteId, invite_type: inviteType, ...extra },
  });
}

export function trackInviteAccepted({ inviteId, inviteType = 'host_to_guest', extra = {} }) {
  if (!inviteId) return;
  sendEvent('invite_accepted', {
    semanticAttributes: { invite_id: inviteId, invite_type: inviteType, ...extra },
  });
}

// Fired when a host creates / confirms an invite. Lets the Airbridge funnel
// measure invite-share-rate (created vs shared vs accepted) and helps spot
// hosts who generate invites but never share them.
export function trackInviteCreated({ inviteId, inviteType = 'host_to_guest', source = 'manual', extra = {} }) {
  if (!inviteId) return;
  sendEvent('invite_created', {
    semanticAttributes: { invite_id: inviteId, invite_type: inviteType, source, ...extra },
  });
}

// Fired when a host taps Share / Copy on an invite. `channel` = 'share_sheet'
// (native OS share) or 'clipboard' (copy fallback). Useful for measuring
// share-method success.
export function trackInviteShared({ inviteId, inviteType = 'host_to_guest', channel = 'share_sheet', extra = {} }) {
  if (!inviteId) return;
  sendEvent('invite_shared', {
    semanticAttributes: { invite_id: inviteId, invite_type: inviteType, channel, ...extra },
  });
}

export function trackQrUnlocked({ inviteId = null, extra = {} } = {}) {
  sendEvent('qr_unlocked', {
    semanticAttributes: { invite_id: inviteId, ...extra },
  });
}

// ---------------------------------------------------------------------------
// Boot-time Airbridge URL parameter capture.
//
// When a user lands on the PWA via an Airbridge tracking link, the URL may
// carry attribution params (e.g. `?airbridge_referrer=…`, `utm_*`). We log
// them so they can be associated with the imminent signup/invite event and
// stripped from the URL on first render so they don't pollute SPA history.
// ---------------------------------------------------------------------------

export function captureAirbridgeContextFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const ctx = {};
    [
      'airbridge_referrer',
      'airbridge_deeplink',
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'utm_term',
    ].forEach((key) => {
      const v = params.get(key);
      if (v) ctx[key] = v;
    });
    if (Object.keys(ctx).length) {
      sessionStorage.setItem('airbridgeContext', JSON.stringify(ctx));
    }
    return ctx;
  } catch {
    return {};
  }
}

export function getStoredAirbridgeContext() {
  try {
    return JSON.parse(sessionStorage.getItem('airbridgeContext') || '{}');
  } catch {
    return {};
  }
}
